import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiBadge, EuiFlexGroup, EuiFlexItem, EuiLink, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiText, EuiTitle, SortDirection } from '@elastic/eui'
import { EuiBasicTableColumn } from '@elastic/eui/src/components/basic_table/basic_table'

import { MasterCampaignPlanStatus } from 'api/interfaces/MasterCampaignPlan'
import { useDeleteMasterCampaignPlanMutation, useGetMasterCampaignPlansQuery, useGetMasterCampaignStubsForAccountQuery } from 'api/rtkQueryApi/platform/masterCampaignPlansApi'
import { RootState } from 'app/rootReducer'
import { AcEuiInMemoryTableWithSearch } from 'components/Basic/AcEuiInMemoryTableWithSearch'
import { reset } from 'features/planner/CreateMasterCampaignPlanSlice'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'
import { getMasterCampaignStubAverageMonthlyBudget, getMasterCampaignStubStartDate } from 'utils/CampaignMathUtils'

import history from '../../services/HistoryService'

interface MasterCampaignPlanItem {
  id: string
  name: string
  accountId: string
  accountName: string
  created: Moment
  status: MasterCampaignPlanStatus
  campaignCount: number
  avgMonthlyBudget: number
  durationMonths: number
  startDate: Moment
}

const getStatusText = (status: MasterCampaignPlanStatus) => {
  switch (status) {
    case MasterCampaignPlanStatus.Processing:
      return 'Processing'
    case MasterCampaignPlanStatus.Migrating:
      return 'Migrating'
    case MasterCampaignPlanStatus.Ready:
      return 'Ready'
    case MasterCampaignPlanStatus.TransferredToDsp:
      return 'Transferred to DSP'
    case MasterCampaignPlanStatus.Deleted:
      return 'Deleted'
  }
}

const StatusBadge = (status: MasterCampaignPlanStatus) => {
  return <EuiBadge color={status === MasterCampaignPlanStatus.Migrating || status === MasterCampaignPlanStatus.Processing ? 'warning' : 'success'}>{getStatusText(status)}</EuiBadge>
}

export const MasterCampaignPlansPage = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount, isStaffUser } = useSelector((state: RootState) => state.app)
  const { data: plans, isFetching: isFetchingPlans } = useGetMasterCampaignPlansQuery({ organizationId: currentOrganization?.id ?? '' })
  const [masterCampaignPlanItems, setMasterCampaignPlanItems] = useState<MasterCampaignPlanItem[]>([])
  const { data: masterCampaignStubs, isFetching: isFetchingMasterCampaignStubs } = useGetMasterCampaignStubsForAccountQuery({ accountId: currentAccount?.id ?? '' }, { skip: !currentAccount?.id || !plans || plans.filter(plan => plan.accountId === currentAccount?.id).length <= 0 })
  const [deleteMasterCampaignPlan, deleteMasterCampaignPlanResult] = useDeleteMasterCampaignPlanMutation()

  const [processingMasterCampaignPlanModalOpen, setProcessingMasterCampaignPlanModalOpen] = useState(false)
  const [selectedMasterCampaignPlanForDeleteModalOpen, setSelectedMasterCampaignPlanForDeleteModalOpen] = useState<MasterCampaignPlanItem | undefined>()

  useEffect(() => {
    if (!currentAccount || !plans) {
      return
    }

    if (plans.length > 0) {
      return
    }

    dispatch(reset())
    history.push('/ai/welcome')
  }, [currentAccount, plans, dispatch])

  useEffect(() => {
    if (!currentAccount || !masterCampaignStubs) {
      return
    }

    const masterCampaignPlanItems = plans
      ?.filter(masterCampaignPlan => masterCampaignPlan.accountId === currentAccount?.id)
      .map(masterCampaignPlan => {
        const masterCampaignStub = masterCampaignStubs.find(s => masterCampaignPlan.id && s.masterCampaignPlanId === masterCampaignPlan.id)
        return {
          id: masterCampaignPlan.id,
          name: masterCampaignPlan.name,
          accountId: masterCampaignPlan.accountId,
          accountName: currentAccount?.name ?? '',
          created: moment(masterCampaignPlan.created),
          campaignCount: masterCampaignPlan.status !== MasterCampaignPlanStatus.Migrating && masterCampaignPlan.status !== MasterCampaignPlanStatus.Processing && masterCampaignStub ? masterCampaignStub.campaignBuilders.length : undefined,
          avgMonthlyBudget: masterCampaignPlan.status !== MasterCampaignPlanStatus.Migrating && masterCampaignPlan.status !== MasterCampaignPlanStatus.Processing && masterCampaignStub ? getMasterCampaignStubAverageMonthlyBudget(masterCampaignStub) : 0,
          durationMonths: masterCampaignPlan?.aiRecommendedStartAndDuration ? 12 : masterCampaignPlan?.aiUsedDurationInMonths,
          status: masterCampaignPlan.status,
          startDate: getMasterCampaignStubStartDate(masterCampaignStub)
        } as MasterCampaignPlanItem
      })

    setMasterCampaignPlanItems(masterCampaignPlanItems ?? [])
  }, [plans, masterCampaignStubs, currentAccount])

  const onMasterCampaignPlanViewClick = (planItem: MasterCampaignPlanItem) => {
    if (!isStaffUser && (planItem.status === MasterCampaignPlanStatus.Migrating || planItem.status === MasterCampaignPlanStatus.Processing)) {
      setProcessingMasterCampaignPlanModalOpen(true)
      return
    }

    history.push(`/ai/${planItem.id}`)
  }

  const columns: EuiBasicTableColumn<MasterCampaignPlanItem>[] = [
    {
      name: 'Plan Name',
      sortable: (ap: MasterCampaignPlanItem) => ap.name,
      render: (ap: MasterCampaignPlanItem) => {
        return (
          <EuiLink onClick={() => onMasterCampaignPlanViewClick(ap)} color='success'>
            {ap.name}
          </EuiLink>
        )
      }
    },
    {
      name: 'Date Created',
      field: 'created',
      render: (created: Moment) => {
        return moment(created).format('MM/DD/YYYY')
      },
      sortable: true
    },
    {
      name: 'Plan Start Date',
      field: 'startDate',
      render: (startDate: Moment) => {
        return moment(startDate).format('MM/DD/YYYY')
      },
      sortable: true
    },
    {
      name: 'Number of Months',
      field: 'durationMonths',
      render: (duration: number) => `${duration}`,
      sortable: true
    },
    {
      name: 'Number of Campaigns',
      field: 'campaignCount',
      render: (campaignCount?: number) => (campaignCount ? campaignCount : ''),
      sortable: true
    },
    {
      name: 'Status',
      field: 'status',
      sortable: true,
      render: (status: MasterCampaignPlanStatus) => {
        return StatusBadge(status)
      }
    },
    {
      name: 'Actions',
      actions: [
        {
          name: 'Delete',
          description: 'Delete this master campaign plan',
          icon: 'trash',
          type: 'icon',
          color: 'danger',
          onClick: masterCampaignPlan => {
            setSelectedMasterCampaignPlanForDeleteModalOpen(masterCampaignPlan)
          },
          enabled: item => item.status === MasterCampaignPlanStatus.Ready,
          available: () => !deleteMasterCampaignPlanResult.isLoading
        }
      ]
    }
  ]

  const sorting = {
    allowNeutralSort: true,
    enableAllColumns: true,
    sort: {
      field: 'created',
      direction: SortDirection.DESC
    }
  }

  const onDeleteMasterCampaignPlanClick = async () => {
    const masterCampaignPlan = selectedMasterCampaignPlanForDeleteModalOpen

    if (!currentAccount || !currentOrganization || !masterCampaignPlan) {
      return
    }

    await deleteMasterCampaignPlan({ organizationId: currentOrganization.id, accountId: currentAccount.id, masterCampaignPlanId: masterCampaignPlan.id })

    setSelectedMasterCampaignPlanForDeleteModalOpen(undefined)
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>Legacy Planner: {currentAccount?.name}</h1>
            </EuiTitle>
            <EuiText>Use the planner to create AI generated marketing campaigns for your client.</EuiText>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          <AcEuiInMemoryTableWithSearch
            searchFields={['name']}
            message={isFetchingPlans || isFetchingMasterCampaignStubs ? 'Loading Master Campaign Plans...' : 'No Master Campaign Plans Found'}
            search={{
              toolsRight: []
            }}
            loading={isFetchingPlans || isFetchingMasterCampaignStubs}
            items={isFetchingPlans || isFetchingMasterCampaignStubs ? [] : masterCampaignPlanItems}
            columns={columns}
            sorting={sorting}
            compressed
          />
        </EuiPanel>
      </EuiPageBody>
      {processingMasterCampaignPlanModalOpen && (
        <AgencyV2ModalOld isLoading={false} title='Master Campaign Plan' onClose={() => setProcessingMasterCampaignPlanModalOpen(false)} confirmButtonText='OK' onConfirmButtonClick={() => setProcessingMasterCampaignPlanModalOpen(false)} bodyHeight='11em'>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>AdCritter AI will now conduct research and build a master campaign to fit the information you provided.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiText>Due to strong demand, this can take several hours. We recommend allowing 24 hours just to be safe.</EuiText>
            </EuiFlexItem>
          </EuiFlexGroup>
        </AgencyV2ModalOld>
      )}
      {selectedMasterCampaignPlanForDeleteModalOpen && (
        <AgencyV2ModalOld isLoading={deleteMasterCampaignPlanResult.isLoading} title='Delete Plan' confirmButtonText='Delete' onConfirmButtonClick={onDeleteMasterCampaignPlanClick} onClose={() => setSelectedMasterCampaignPlanForDeleteModalOpen(undefined)} showCancelButton>
          <EuiText>Are you sure you want to delete this master campaign plan?</EuiText>
        </AgencyV2ModalOld>
      )}
    </EuiPage>
  )
}
